/**
 * Portal event names — single source of truth.
 *
 * ─── Naming convention ────────────────────────────────────────────────
 *   {domain}.{action}
 *
 *   - domain = singular lowercase noun (dashboard, notebook, pipeline, ...)
 *   - action = past-tense verb for completions (created, viewed, deleted)
 *   - hyphens for multi-word actions (filter-applied, run-triggered)
 *
 * ─── Usage ───────────────────────────────────────────────────────────
 *   import { Events } from '@/lib/analytics/events';
 *   import { tracker } from '@/lib/analytics/tracker';
 *
 *   tracker.track(Events.DASHBOARD_VIEWED, { entity_id: dashboardId });
 *
 * Using constants instead of string literals gives us:
 *   - TypeScript catches typos
 *   - grep on this file shows "everything we track"
 *   - Rename refactors are safe
 *   - One place to audit the taxonomy
 *
 * ─── Adding a new event ──────────────────────────────────────────────
 *   1. Add a constant here
 *   2. Call tracker.track(Events.NEW_THING) at the call site
 *   3. That's it — no backend changes, no schema migration
 *
 * The `properties` struct in the backend schema grows organically via
 * `schema_evolution: allow` as new events introduce new property keys.
 */
export const Events = {
  // ── Navigation / page views ──────────────────────────────────────────
  PAGE_VIEWED: 'page.viewed',

  // ── Authentication ───────────────────────────────────────────────────
  AUTH_LOGGED_IN: 'auth.logged-in',
  AUTH_LOGGED_OUT: 'auth.logged-out',
  AUTH_SIGNED_UP: 'auth.signed-up',

  // ── Dashboards (BI) ──────────────────────────────────────────────────
  DASHBOARD_VIEWED: 'dashboard.viewed',
  DASHBOARD_CREATED: 'dashboard.created',
  DASHBOARD_UPDATED: 'dashboard.updated',
  DASHBOARD_DELETED: 'dashboard.deleted',
  DASHBOARD_SHARED: 'dashboard.shared',
  DASHBOARD_FAVORITED: 'dashboard.favorited',
  DASHBOARD_UNFAVORITED: 'dashboard.unfavorited',
  DASHBOARD_FILTER_APPLIED: 'dashboard.filter-applied',
  DASHBOARD_FILTER_CLEARED: 'dashboard.filter-cleared',
  DASHBOARD_EDIT_MODE_ENTERED: 'dashboard.edit-mode-entered',
  CHART_DRILLED_DOWN: 'chart.drilled-down',

  // ── Notebooks ────────────────────────────────────────────────────────
  NOTEBOOK_OPENED: 'notebook.opened',
  NOTEBOOK_CREATED: 'notebook.created',
  NOTEBOOK_UPDATED: 'notebook.updated',
  NOTEBOOK_DELETED: 'notebook.deleted',
  NOTEBOOK_CELL_EXECUTED: 'notebook.cell-executed',
  NOTEBOOK_CELL_ADDED: 'notebook.cell-added',
  NOTEBOOK_CELL_DELETED: 'notebook.cell-deleted',

  // ── Pipelines (visual) ───────────────────────────────────────────────
  PIPELINE_BUILDER_OPENED: 'pipeline.builder-opened',
  PIPELINE_CREATED: 'pipeline.created',
  PIPELINE_UPDATED: 'pipeline.updated',
  PIPELINE_DELETED: 'pipeline.deleted',
  PIPELINE_RUN_TRIGGERED: 'pipeline.run-triggered',
  PIPELINE_CANVAS_SAVED: 'pipeline.canvas-saved',

  // ── Data sources ─────────────────────────────────────────────────────
  DATASOURCE_CREATED: 'datasource.created',
  DATASOURCE_UPDATED: 'datasource.updated',
  DATASOURCE_DELETED: 'datasource.deleted',
  DATASOURCE_CONNECTION_TESTED: 'datasource.connection-tested',
  DATASOURCE_SYNC_TRIGGERED: 'datasource.sync-triggered',

  // ── Query explorer + transforms ──────────────────────────────────────
  QUERY_EXECUTED: 'query.executed',
  TRANSFORM_CREATED: 'transform.created',
  TRANSFORM_UPDATED: 'transform.updated',
  TRANSFORM_DELETED: 'transform.deleted',
  TRANSFORM_EXECUTED: 'transform.executed',

  // ── Catalog ──────────────────────────────────────────────────────────
  CATALOG_TABLE_VIEWED: 'catalog.table-viewed',
  CATALOG_NAMESPACE_EXPANDED: 'catalog.namespace-expanded',
  CATALOG_POLICY_CREATED: 'catalog.policy-created',
  CATALOG_POLICY_UPDATED: 'catalog.policy-updated',
  CATALOG_POLICY_DELETED: 'catalog.policy-deleted',
} as const;

export type EventName = (typeof Events)[keyof typeof Events];
